// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Lo Storico e le stampe"
// Centro assistenza → guida per amministratori e responsabili (HACCP).
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Etichette dal codice.
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Schermata ...... App.js, Storico — titolo "Storico monitoraggi".
//   Filtri ......... card "Filtri ricerca": Ambito, Tipo attività,
//                    Area / reparto, Oggetto, Eseguito da, Responsabile
//                    assegnato, Dal / Al (predefinito: dal primo del mese a
//                    oggi), pulsante Cerca. Le due tendine-persona sono LENTI
//                    DIVERSE: "Eseguito da" = chi ha FIRMATO la registrazione;
//                    "Responsabile assegnato" (ago 2026, richiesta cliente) =
//                    la scheda del titolare a scadenziario, supplenti inclusi
//                    — stessa semantica della verifica a blocchi. Entrambe
//                    finiscono dichiarate nella testata di stampa.
//   Risultati ...... contatore "N record trovati" + pulsante "Stampa".
//                    Card: chip esito (Conforme/Non conforme/N.A.), data · ora,
//                    "Verificato"/"Non verificato", chip viola del registro
//                    speciale (TARATURA/MANUTENZIONE/ANALISI/VERIFICA);
//                    riquadro "Valore: X · min · max"; in basso l'operatore e i
//                    pulsanti "Allegati (N)" e "Correggi".
//   Correzione ..... ModalCorreggi, titolo "Correggi monitoraggio": Data, Ora,
//                    Esito (CONFORME/NON CONFORME/NON APPLICABILE), Valore
//                    misurato, Note, "Motivazione correzione *" obbligatoria.
//                    Regole (identiche a backend PUT /attivita/:id):
//                    admin sempre; operatore solo proprie + non verificate +
//                    entro GIORNI_FINESTRA_CORREZIONE = 7. Il pulsante spento
//                    spiega il motivo nel suggerimento del mouse.
//   Stampa ......... stampaReport(): finestra "Registro monitoraggi HACCP",
//                    testata con logo e filtri DELLA RICERCA FATTA
//                    (filtriUsati, non le tendine correnti), totali, righe
//                    raggruppate per ambito, firma grafica dell'operatore
//                    accanto alla riga, spunta di verifica con apice (i) se i
//                    verificatori del periodo sono 2+, blocchi firma in calce.
//   Correzioni ..... banner Dashboard (admin): "N registrazioni corrette da
//                    operatori dall'ultimo controllo" + "Vedi correzioni" →
//                    sezione "Storico correzioni" (log campo per campo,
//                    prima → dopo, con motivazione). Aprire la sezione marca
//                    le correzioni come viste e azzera il banner.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 's0', label: 'Il registro da mettere sul tavolo' },
  { id: 's1', label: '1. Cercare nel registro' },
  { id: 's2', label: '2. Leggere una riga' },
  { id: 's3', label: '3. Correggere dallo Storico' },
  { id: 's4', label: '4. La stampa per l’ispezione' },
  { id: 's5', label: '5. Le correzioni sotto controllo' },
  { id: 's6', label: 'Le trappole' },
  { id: 's7', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const ChipFinto = ({ children, tono = 'neutro', pieno }) => {
  const c = {
    verde:   { fg: LT.verde,   bg: LT.verdeSoft },
    rosso:   { fg: LT.rosso,   bg: LT.rossoSoft },
    blu:     { fg: LT.blu,     bg: LT.bluSoft },
    viola:   { fg: LT.viola,   bg: LT.violaSoft },
    arancio: { fg: LT.arancio, bg: LT.arancioSoft },
    neutro:  { fg: LT.n700,    bg: LT.n100 },
  }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999,
      background: pieno ? c.fg : c.bg, color: pieno ? '#fff' : c.fg,
      fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const BtnFinto = ({ children, tono = 'neutro', pieno }) => {
  const c = { verde: LT.verde, blu: LT.blu, arancio: LT.arancio, neutro: LT.n400 }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      padding: '8px 13px', borderRadius: LT.r.md, fontSize: 12.5, fontWeight: 700,
      background: pieno ? c : LT.n0,
      color: pieno ? '#fff' : (tono === 'neutro' ? LT.n700 : c),
      border: `1px solid ${pieno || tono !== 'neutro' ? c : LT.n300}`,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

// Una riga dello storico, come compare fra i risultati.
const CardStorico = () => (
  <div style={{
    background: LT.n0, border: `1px solid ${LT.n150}`,
    borderLeft: `4px solid ${LT.rosso}`,
    borderRadius: LT.r.lg, padding: 14,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
      <ChipFinto tono="rosso" pieno>Non conforme</ChipFinto>
      <ChipFinto>18/08/2026 · 06:12</ChipFinto>
      <ChipFinto tono="blu">Verificato</ChipFinto>
    </div>
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{
        width: 40, height: 40, borderRadius: LT.r.md, flexShrink: 0,
        background: LT.cat.temperature.bg, color: LT.cat.temperature.c,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Ico d={I[LT.cat.temperature.icon] || I.list} size={20} stroke={LT.cat.temperature.c} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>Cella 1 — sale</div>
        <div style={{ fontSize: 12.5, color: LT.n600, marginTop: 3 }}>
          Reparto salatura <span style={{ color: LT.n300 }}>·</span> Controllo temperatura
        </div>
      </div>
    </div>
    <div style={{
      marginTop: 10, borderRadius: LT.r.md, padding: '8px 12px',
      fontSize: 13, fontFamily: LT.mono,
      background: LT.rossoSoft, color: LT.rosso, border: '1px solid #fecaca',
    }}>
      Valore: <strong>6,8</strong> · min 0 · max 4
    </div>
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginTop: 12, gap: 8, flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 13, color: LT.n600, fontWeight: 500 }}>Mario Rossi</span>
      <span style={{ display: 'flex', gap: 6 }}>
        <BtnFinto>Allegati (1)</BtnFinto>
        <BtnFinto tono="blu">Correggi</BtnFinto>
      </span>
    </div>
  </div>
);

export default function Storico() {
  return (
    <GuidaLayout
      titolo="Lo Storico e le stampe"
      sottotitolo="Tutte le registrazioni in un posto solo: come cercarle, come correggere quella sbagliata, e come tirarne fuori il registro stampato che si mette sul tavolo quando arriva l'ispezione."
      durata="~ 12 minuti"
      destinatario="Per amministratori e responsabili"
      sezioni={SEZIONI}
    >

      {/* s0 */}
      <H id="s0">Il registro da mettere sul tavolo</H>

      <P>
        Ogni registrazione fatta dalla schermata delle attività finisce qui, nello{' '}
        <Code>Storico monitoraggi</Code>: conformi, non conformi, N.A., con o senza
        verifica. È la memoria del sistema — e il giorno dell'ispezione è la schermata da
        cui esce il documento che conta, il <Strong>Registro monitoraggi HACCP</Strong>{' '}
        stampato con firme e totali.
      </P>
      <P>
        Lo Storico lo vedono tutti gli operatori; questa guida però parla soprattutto a chi
        deve <Strong>rispondere</Strong> del registro: cercare in fretta, sistemare gli
        errori nel modo giusto, e stampare senza sorprese.
      </P>

      {/* s1 */}
      <H id="s1" cat="temperature">1. Cercare nel registro</H>

      <P>
        In cima c'è la card <Strong>Filtri ricerca</Strong>: <Strong>Ambito</Strong>,{' '}
        <Strong>Tipo attività</Strong>, <Strong>Area / reparto</Strong>,{' '}
        <Strong>Oggetto</Strong>, le due tendine-persona di cui sotto, e il periodo{' '}
        <Strong>Dal / Al</Strong>. Niente compare finché non premete{' '}
        <Tasto>Cerca</Tasto> — e ogni volta che cambiate un filtro va premuto di nuovo.
      </P>

      <Callout kind="info" titolo="Due lenti sulle persone: chi ha firmato, e di chi è la scheda">
        <Strong>Eseguito da</Strong> filtra per chi ha <Strong>materialmente
        firmato</Strong> la registrazione — è la domanda dell'ispettore, «chi l'ha
        firmato?» — ed elenca solo chi ne ha firmata almeno una.{' '}
        <Strong>Responsabile assegnato</Strong> risponde all'altra domanda, quella
        della scheda cartacea: tutte le attività che lo scadenziario{' '}
        <Strong>intesta</Strong> a una persona, <em>compresi i giorni firmati dai
        supplenti</em> — la stessa logica della verifica a blocchi. Si possono anche
        combinare: la scheda di Mario, nei soli giorni firmati dal suo supplente, è a
        due tendine di distanza.
      </Callout>

      <Callout kind="tip" titolo="Il periodo parte dal mese corrente">
        All'apertura <Strong>Dal / Al</Strong> copre dal primo del mese a oggi. Per
        un'ispezione che guarda indietro di un anno, la prima mossa è allargare il{' '}
        <Strong>Dal</Strong> — altrimenti si stampa un registro di tre settimane convinti
        di aver stampato tutto.
      </Callout>

      {/* s2 */}
      <H id="s2" cat="temperature">2. Leggere una riga</H>

      <MockFrame
        url="app.labthree.it — Storico monitoraggi"
        markers={[
          { n: 1, top: '12.8%', left: '23%', titolo: 'Il contatore e la stampa',
            testo: 'Quante righe ha trovato la ricerca. Il pulsante Stampa compare solo qui, dopo una ricerca con risultati: la stampa è sempre la fotografia di questa lista.' },
          { n: 2, top: '32.7%', left: '51%', titolo: 'Esito, data, verifica',
            testo: 'I contrassegni dicono tutto in una riga: com’è andata, quando, e se il responsabile l’ha già riletta.' },
          { n: 3, top: '81.6%', left: '63%', titolo: 'Allegati e Correggi',
            testo: 'Allegati per i documenti tecnici (tarature, analisi). Correggi apre la correzione — e quando è spento, tenendoci sopra il mouse spiega perché.' },
        ]}
      >
        <Schermo>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 12,
          }}>
            <ChipFinto>14 record trovati</ChipFinto>
            <BtnFinto>Stampa</BtnFinto>
          </div>
          <CardStorico />
        </Schermo>
      </MockFrame>

      <P>
        Il bordo a sinistra ripete l'esito — verde, rosso, grigio per gli N.A. — e i
        contrassegni in alto dicono lo stato: <ChipFinto tono="blu">Verificato</ChipFinto>{' '}
        oppure <ChipFinto>Non verificato</ChipFinto>. Le attività tecniche portano anche il
        contrassegno viola del loro registro — <ChipFinto tono="viola">TARATURA</ChipFinto>,{' '}
        <ChipFinto tono="viola">ANALISI</ChipFinto>… — e il pulsante{' '}
        <Tasto>Allegati</Tasto>, col conteggio dei documenti già caricati: è da qui che si
        aggiunge il rapporto arrivato in ritardo.
      </P>

      {/* s3 */}
      <H id="s3" cat="pulizie">3. Correggere dallo Storico</H>

      <P>
        <Tasto>Correggi</Tasto> apre la finestra <Code>Correggi monitoraggio</Code>: si
        possono sistemare <Strong>Data</Strong>, <Strong>Ora</Strong>,{' '}
        <Strong>Esito</Strong> (anche riportarlo a N.A.), <Strong>Valore misurato</Strong>{' '}
        e <Strong>Note</Strong>. Un campo però non è opzionale: la{' '}
        <Strong>Motivazione correzione</Strong>. Senza spiegare il perché, il salvataggio
        non parte.
      </P>

      <Passo n="1" titolo="Chi può correggere cosa">
        <P style={{ margin: '4px 0' }}>
          Un amministratore corregge tutto. Un operatore solo le{' '}
          <Strong>proprie</Strong> registrazioni, <Strong>non ancora verificate</Strong>,
          degli ultimi <Strong>sette giorni</Strong>. Fuori da queste condizioni il
          pulsante resta visibile ma spento, e il suggerimento del mouse dice quale delle
          tre è saltata.
        </P>
      </Passo>

      <Passo n="2" titolo="Niente sparisce">
        <P style={{ margin: '4px 0' }}>
          La correzione non sovrascrive nel silenzio: ogni campo toccato finisce nel log
          delle correzioni con il valore di prima, quello di dopo, chi l'ha cambiato e la
          motivazione. È quello che rende la correzione difendibile davanti a un
          controllo, invece di sospetta.
        </P>
      </Passo>

      <Callout kind="warn" titolo="Le stesse regole valgono anche sul server">
        I limiti non sono solo dell'interfaccia: il server rifiuta una correzione fuori
        regola anche se arrivasse per altre strade. E la coerenza fra esito e valore vale
        come in registrazione — un 6,8 con limiti 0–4 non si può salvare come conforme
        nemmeno correggendo.
      </Callout>

      {/* s4 */}
      <H id="s4" cat="produzione">4. La stampa per l'ispezione</H>

      <P>
        Con i risultati davanti, <Tasto>Stampa</Tasto> apre il{' '}
        <Strong>Registro monitoraggi HACCP</Strong> in una finestra dedicata, pronto per la
        stampante o per il salvataggio in PDF. Non è la schermata «stampata»: è un
        documento con una struttura sua.
      </P>

      <Passo n="1" titolo="La testata dichiara cosa state guardando">
        <P style={{ margin: '4px 0' }}>
          Logo e ragione sociale dell'azienda, il periodo e <Strong>i filtri con cui è
          stata fatta la ricerca</Strong>. Se dopo la ricerca avete toccato le tendine
          senza ripremere Cerca, in testata finiscono comunque i filtri veri delle righe
          stampate — il documento non può contraddire se stesso.
        </P>
      </Passo>

      <Passo n="2" titolo="I totali e i gruppi">
        <P style={{ margin: '4px 0' }}>
          In cima i conti: totale, conformi, non conformi, N.A., verificate. Poi le righe,
          raggruppate per ambito e in ordine di data — le temperature con le temperature,
          le pulizie con le pulizie.
        </P>
      </Passo>

      <Passo n="3" titolo="Le firme">
        <P style={{ margin: '4px 0' }}>
          Accanto a ogni riga c'è la <Strong>firma grafica dell'operatore</Strong>, se
          caricata sul suo profilo. Le righe verificate portano la spunta <Code>✓</Code>;
          quando i verificatori del periodo sono più d'uno, la spunta ha un richiamo
          numerico — <Code>✓⁽¹⁾</Code> — che rimanda ai <Strong>blocchi firma in
          calce</Strong>, uno per verificatore, con nome, data dell'ultima verifica e
          firma grafica.
        </P>
      </Passo>

      <Callout kind="tip" titolo="Per l'ispezione: un registro per ambito">
        Tecnicamente si può stampare tutto insieme, ma il documento più comodo da
        sfogliare — e da far sfogliare — è un registro per ambito sul periodo richiesto:
        una ricerca «Temperature, dal… al…», stampa, poi la successiva. La testata di
        ciascuno dichiara il proprio perimetro.
      </Callout>

      {/* s5 */}
      <H id="s5" cat="manutenzioni">5. Le correzioni sotto controllo</H>

      <P>
        Le correzioni degli operatori non passano inosservate. Sulla{' '}
        <Strong>Dashboard</Strong> dell'amministratore compare una striscia arancione:
      </P>

      <MockFrame
        url="app.labthree.it — Dashboard"
        didascalia="Il banner si azzera da solo quando aprite il log: «visto» significa proprio visto."
      >
        <Schermo>
          <div style={{
            background: LT.arancioSoft, border: `1px solid ${LT.arancio}33`,
            borderRadius: LT.r.md, padding: '12px 14px',
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <Ico d={I.edit} size={18} stroke={LT.arancio} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 200, fontSize: 13, color: LT.n800 }}>
              <strong style={{ color: LT.arancio }}>2 registrazioni corrette</strong>{' '}
              da operatori dall'ultimo controllo{' '}
              <span style={{ color: LT.n500 }}>— ultima: 19/08/2026, 14:32</span>
            </span>
            <BtnFinto tono="arancio" pieno>Vedi correzioni</BtnFinto>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        <Tasto>Vedi correzioni</Tasto> porta allo <Code>Storico correzioni</Code>, il log
        completo: per ogni correzione, il campo toccato — Data, Ora, Esito, Valore, Note —
        con il valore <Strong>prima</Strong>, quello <Strong>dopo</Strong>, chi l'ha
        fatta e la motivazione scritta. Aprire la sezione marca le correzioni come viste,
        e il banner sparisce fino alla prossima.
      </P>

      {/* s6 */}
      <H id="s6">Le trappole</H>

      <Callout kind="warn" titolo="La stampa fotografa l'ultima ricerca">
        Cambiare una tendina <Strong>non</Strong> aggiorna la lista: serve ripremere{' '}
        <Tasto>Cerca</Tasto>. Il documento stampato corrisponde sempre all'ultima ricerca
        fatta — la testata lo dichiara — quindi se avete appena allargato il periodo e la
        stampa sembra corta, quasi sempre manca un Cerca.
      </Callout>

      <Callout kind="warn" titolo="La finestra di stampa bloccata">
        La stampa si apre in una finestra nuova, e certi browser la bloccano al primo
        tentativo. Se non si apre e compare l'avviso, consentite le finestre pop-up per
        LabThree e ripremete Stampa: è un'impostazione del browser, non un guasto.
      </Callout>

      <Callout kind="warn" titolo="Firme mancanti in stampa">
        Se accanto alle righe non compare la firma di un operatore, non è un errore del
        registro: quella persona non ha una firma grafica caricata sul profilo. Si carica
        una volta sola, dall'anagrafica dell'organico, e da lì in poi compare su tutte le
        stampe.
      </Callout>

      {/* s7 */}
      <H id="s7">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="Posso avere il registro in PDF invece che su carta?">
          Sì, ed è la stessa strada: <Tasto>Stampa</Tasto>, e nella finestra di stampa del
          browser scegliete «Salva come PDF» al posto della stampante. Il documento è
          identico.
        </FAQ>

        <FAQ q="Un operatore può vedere le registrazioni dei colleghi?">
          Sì: lo Storico mostra il registro dell'azienda, che è comune per definizione. La
          differenza è nel <Strong>correggere</Strong> — lì ognuno arriva solo alle
          proprie, non verificate, degli ultimi sette giorni.
        </FAQ>

        <FAQ q="Ho corretto una registrazione: l'ispettore se ne accorge?">
          Il registro stampato mostra i valori correnti. Ma il log delle correzioni
          conserva tutto — prima, dopo, chi e perché — ed è un punto di forza, non una
          debolezza: un sistema che traccia le proprie correzioni è esattamente quello che
          un controllo vuole vedere. Correggere è normale; cancellare le tracce no, e qui
          non si può.
        </FAQ>

        <FAQ q="Perché nella tendina «Eseguito da» manca un operatore?">
          Perché non ha ancora firmato nessuna registrazione: la tendina elenca solo chi
          compare davvero nel registro. Appena firma la prima, entra in lista.
        </FAQ>

        <FAQ q="Le attività N.A. finiscono nel registro stampato?">
          Sì, con il loro contrassegno e nel conteggio dei totali. È il punto degli N.A.:
          documentare che quel giorno il controllo non si poteva fare, invece di lasciare
          un buco che a distanza di mesi non si sa più spiegare.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.bluSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.arrowRight} size={20} stroke={LT.blu} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.blu, fontSize: 14, marginBottom: 4 }}>
            La guida dopo questa
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            <Strong>Organico e PIN</Strong>: le persone dietro le firme — inserirle,
            dare loro un PIN, e caricare la firma grafica che finisce sulle stampe.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
